import React, { useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Drawer from "@mui/material/Drawer";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { OptionRow } from "/imports/ui/components/booking";

const ALL_LABEL = "All locations";

// The club's venues, as they appear in the loaded timetable. Picking one narrows
// the session list to that venue; the picks already made are never hidden by it.
function SessionsLocationFilter({ options, value, onChange, sx }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);
  const open = !!anchorEl;
  const close = () => setAnchorEl(null);

  const selected = options.find((o) => o._id === value) || null;

  const pick = (id) => {
    onChange(id);
    close();
  };

  const rows = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, overflowY: "auto" }}>
      <OptionRow label={ALL_LABEL} selected={!value} onClick={() => pick(null)} />
      {options.map((l) => (
        <OptionRow key={l._id} label={l.name} selected={value === l._id} onClick={() => pick(l._id)} />
      ))}
    </Box>
  );

  return (
    <>
      {/* Sits next to the pick-count Chip, so it borrows the Chip's height and
          radius — at any other size the two read as unrelated controls. */}
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.25,
          height: "2rem",
          minWidth: 0,
          flexShrink: 1,
          borderRadius: "1rem",
          border: "1.5px solid",
          maxWidth: { sm: "14rem" },
          borderColor: selected ? "primary.main" : "divider",
          bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.1) : "transparent"),
          ...sx
        }}
      >
        <PlaceOutlinedIcon sx={{ flexShrink: 0, fontSize: "1rem", color: selected ? "primary.main" : "text.secondary" }} />
        <Typography
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: "left",
            fontSize: "0.8125rem",
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selected ? "primary.main" : "text.primary"
          }}
        >
          {selected ? selected.name : ALL_LABEL}
        </Typography>
        <KeyboardArrowDownIcon sx={{ flexShrink: 0, fontSize: "1rem", color: "text.secondary" }} />
      </ButtonBase>

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={close}
          PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "75vh", px: 2.5, pt: 2, pb: 3 } }}>
          <Box sx={{ width: "2.5rem", height: "0.25rem", borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }}>Location</Typography>
            <IconButton size="small" onClick={close} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {rows}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { mt: 0.5, p: 1.5, minWidth: "16rem", maxHeight: "24rem", borderRadius: 3 } }}>
          {rows}
        </Popover>
      )}
    </>
  );
}

export default SessionsLocationFilter;
